import { FC, useMemo, ReactElement } from 'react';

import { Heading, HEADING_SIZE, HEADING_TAG, Stepper } from '@amzn/sss-website-theme';

import styles from './StaticWorkflowTracker.module.scss';

/**
 * Represents a single step in the static workflow tracker.
 */
export interface StaticWorkflowStep {
  /** The display label for the step */
  stepLabel: string;
  /** The React component to render for this step */
  stepComponent: ReactElement;
}

/**
 * Props for the StaticWorkflowTracker component.
 */
export interface StaticWorkflowTrackerProps {
  /** The title displayed at the top of the workflow */
  workflowTitle: string;
  /** Array of workflow steps to display */
  workflowSteps: StaticWorkflowStep[];
  /** Zero-based index of the currently active step */
  activeStep: number;
}

/**
 * A static workflow tracker component that displays a stepper and renders the active step.
 * "Static" means it renders steps based on props without allowing manual step navigation.
 *
 * @param props - The component props
 * @returns The rendered workflow tracker or null if no valid step is found
 */
export const StaticWorkflowTracker: FC<StaticWorkflowTrackerProps> = ({ workflowTitle, workflowSteps, activeStep }) => {
  const currentStepComponent = useMemo(() => workflowSteps[activeStep]?.stepComponent, [workflowSteps, activeStep]);

  const stepperSteps = useMemo(
    () =>
      workflowSteps.map((step, stepIndex) => ({
        title: `${stepIndex + 1}`,
        label: step.stepLabel,
      })),
    [workflowSteps]
  );

  if (!currentStepComponent) {
    return null;
  }

  return (
    <>
      <header className={styles.header}>
        <Heading className={styles.heading} size={HEADING_SIZE.h3} as={HEADING_TAG.H3}>
          {workflowTitle}
        </Heading>
        <Stepper className={styles.stepper} activeStep={activeStep} steps={stepperSteps} />
      </header>
      {currentStepComponent}
    </>
  );
};
